import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/shadcn/dialog';
import { PersonIcon, CubeIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Button } from '@/components/shadcn/button';
import { capitalize, cn } from '@/lib/utils';
import { useEffect, useState, Suspense } from 'react';
import { ProfileTab } from './profile-tab/profile-tab';
import { UsersTab } from './users-tab/users-tab';
import { OrganizationTab } from './ogranization-tab/organization-tab';
import { GrantsTab } from './grants-tab/grants-tab';
import { PostsTab } from './posts-tab/posts-tab';
import {
  hasAdminRole,
  hasOrgManagerRole,
  hasSocialMediaManagerRole,
} from '@/lib/auth-service';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import IntegrationsTab from './integrations-tab/integrations-tab';
import { useRouter } from 'next/router';
import {
  IS_HANDLE_CALLBACK_QUERY_PARAM,
  SOCIAL_PLATFORM_QUERY_PARAM,
} from '@/consts/social-media';
import { TeamMembersTab } from './team-members-tab/team-members-tab';
import { Spinner } from '@/components/basic/spinner';
import { logInfo } from '@/lib/rum';
import { MaggieTab } from './maggie-tab/maggie-tab';
import { BrandTab } from './brand-tab/brand-tab';
import { SessionContextUpdate } from 'supertokens-auth-react/lib/build/recipe/session/types';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const socialPlatformQueryParamMap: any = {
  meta: 'Meta',
  linkedin: 'LinkedIn',
  google: 'Google',
};

export function Settings() {
  const isGsuiteIntegrationEnabled =
    !!useFeatureFlagEnabled('gsuite-integration');
  const session = useSessionContext();
  const router = useRouter();
  const integrationItems = isGsuiteIntegrationEnabled
    ? ['Meta', 'LinkedIn', 'Google']
    : ['Meta', 'LinkedIn'];

  const accountMenuItems = [
    {
      label: 'Profile',
      permission: () => true,
    },
    {
      label: 'Organization',
      permission: (session: SessionContextUpdate) =>
        hasOrgManagerRole(session?.accessTokenPayload['st-role']?.v),
    },
    {
      label: 'Team Members',
      permission: (session: SessionContextUpdate) =>
        hasAdminRole(session?.accessTokenPayload['st-role']?.v),
    },
    {
      label: 'Users',
      permission: (session: SessionContextUpdate) =>
        hasOrgManagerRole(session?.accessTokenPayload['st-role']?.v),
    },
    {
      label: 'Brand',
      permission: (session: SessionContextUpdate) =>
        hasSocialMediaManagerRole(session?.accessTokenPayload['st-role']?.v),
    },
    {
      label: 'Grant',
      permission: (session: SessionContextUpdate) =>
        hasAdminRole(session?.accessTokenPayload['st-role']?.v),
    },
    {
      label: 'Maggie',
      permission: (session: SessionContextUpdate) =>
        hasAdminRole(session?.accessTokenPayload['st-role']?.v),
    },
    {
      label: 'Posts',
      permission: (session: SessionContextUpdate) =>
        hasSocialMediaManagerRole(session?.accessTokenPayload['st-role']?.v),
    },
  ];

  const [tab, setTab] = useState('Profile');

  useEffect(() => {
    const settingsTab = router.query['settingstab'];
    const isHandleCallback = router.query[IS_HANDLE_CALLBACK_QUERY_PARAM];
    const socialMediaPlatform = router.query[SOCIAL_PLATFORM_QUERY_PARAM];
    logInfo('Rendering settings modal and query params change', {
      query: router.query,
    });
    if (isHandleCallback && socialMediaPlatform) {
      setTab(socialPlatformQueryParamMap[socialMediaPlatform as string]);
    }

    const accountItem = accountMenuItems.find(
      (item) => item.label === capitalize(settingsTab as string)
    );
    if (settingsTab && socialPlatformQueryParamMap[settingsTab as string]) {
      setTab(socialPlatformQueryParamMap[settingsTab as string]);
    } else if (
      settingsTab &&
      accountItem?.permission(session as SessionContextUpdate)
    ) {
      setTab(capitalize(settingsTab as string));
    }
  }, [router.query]);

  if (session.loading || !session.doesSessionExist) {
    return null;
  }

  return (
    <DialogContent className='max-h-screen max-w-4xl overflow-auto md:max-h-[90vh]'>
      <DialogHeader className='relative'>
        <DialogClose className='absolute right-2 top-2 rounded-sm border-none opacity-90 ring-transparent transition-opacity hover:opacity-60 focus:ring-transparent disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'>
          <Cross2Icon className='h-5 w-5 text-black' />
          <span className='sr-only'>Close</span>
        </DialogClose>
        <DialogTitle className='pb-8 text-xl font-medium'>Settings</DialogTitle>
        <div className='flex flex-col sm:flex-row sm:gap-16'>
          <div className='flex flex-col gap-8'>
            <div className='flex'>
              <CubeIcon className='mt-0.5 h-4 w-4 text-gray-settings' />
              <div className='text- flex flex-col gap-3'>
                <p className='pl-2 font-medium text-gray-settings'>
                  INTEGRATIONS
                </p>
                {integrationItems.map((item, i) => (
                  <Button
                    key={i}
                    variant='ghost'
                    className={cn(
                      'h-auto justify-start p-2 font-semibold capitalize leading-none text-neutral-900 hover:bg-gray-settings-button',
                      item === tab && 'bg-gray-settings-button'
                    )}
                    onClick={() => {
                      setTab(item);
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </div>
            </div>
            <div className='flex'>
              <PersonIcon className='mt-0.5 h-[16px] w-[16px] text-gray-settings' />
              <div className='text- flex flex-col gap-3'>
                <p className='pl-2 font-medium text-gray-settings'>ACCOUNT</p>
                {accountMenuItems.map(
                  (item, i) =>
                    item.permission(session) && (
                      <Button
                        key={i}
                        variant='ghost'
                        className={cn(
                          'h-auto justify-start p-2 font-semibold leading-none text-neutral-900 hover:bg-gray-settings-button',
                          item.label === tab && 'bg-gray-settings-button'
                        )}
                        onClick={() => {
                          setTab(item.label);
                        }}
                      >
                        {item.label}
                      </Button>
                    )
                )}
              </div>
            </div>
          </div>
          <hr className='my-10 sm:hidden' />
          <div className='grow'>
            {tab === 'Profile' && <ProfileTab />}
            {tab === 'Users' && <UsersTab />}
            {tab === 'Organization' && <OrganizationTab />}
            {tab === 'Brand' && (
              <Suspense fallback={<Spinner />}>
                <BrandTab />
              </Suspense>
            )}
            {tab === 'Grant' && <GrantsTab />}
            {tab === 'Maggie' && <MaggieTab />}
            {tab === 'Posts' && <PostsTab />}
            {(tab === 'Meta' || tab === 'LinkedIn' || tab === 'Google') && (
              <IntegrationsTab currentTab={tab} />
            )}
            {tab === 'Team Members' && (
              <Suspense fallback={<Spinner />}>
                <TeamMembersTab />
              </Suspense>
            )}
          </div>
        </div>
      </DialogHeader>
    </DialogContent>
  );
}
